import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";

import { usePageTitle } from "../lib/usePageTitle";

const EASE = [0.16, 1, 0.3, 1];
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/* ================================================================== */
/*  /strategy-brief                                                    */
/*                                                                     */
/*  Interactive founder-led warm-up questionnaire. One question per    */
/*  screen. Feels like a private note from Krishna, not a form. Sits   */
/*  after the CallModal submission on /growth-partnership.             */
/* ================================================================== */

const QUESTIONS = [
  {
    key: "what_you_sell",
    label: "First, in your own words",
    prompt: "What do you sell, and who is it for?",
    placeholder: "We help early stage founders in fintech run private equity style fundraising rounds...",
    hint: "One or two sentences is perfect. This helps me show up ready.",
    optional: false,
    long: true,
  },
  {
    key: "tried_so_far",
    label: "Reality check",
    prompt: "What have you tried so far to reach them?",
    placeholder: "Cold email, LinkedIn DMs, a paid ads test in Q3, warm referrals from investors...",
    hint: "No judgement. Just the truth of where you are today.",
    optional: false,
    long: true,
  },
  {
    key: "worth_the_call",
    label: "The point of us talking",
    prompt: "What would make this call feel worth thirty minutes of your time?",
    placeholder: "Walking away with a clear picture of whether this could work for my firm this quarter...",
    hint: "Set the bar. I will meet it or tell you honestly if we should not run this.",
    optional: false,
    long: true,
  },
  {
    key: "where_to_look",
    label: "Where I can meet you first",
    prompt: "Where can I look at your work before we speak?",
    placeholder: "https://your-firm.com or your LinkedIn URL",
    hint: "Website, LinkedIn, or a recent piece. Optional. Skip if you would rather I come in cold.",
    optional: true,
    long: false,
  },
  {
    key: "how_you_heard",
    label: "Purely curious",
    prompt: "How did you hear about Get Virtual Growth?",
    placeholder: "A search, a referral, a Field Note, LinkedIn...",
    hint: "Optional. Helps me thank the right people.",
    optional: true,
    long: false,
  },
];

export default function StrategyBrief() {
  usePageTitle("Strategy Brief . Get Virtual Growth");
  const [params] = useSearchParams();
  const initialEmail = (params.get("e") || "").trim();
  const [email, setEmail] = useState(initialEmail);
  const [askingEmail, setAskingEmail] = useState(!initialEmail);
  const [idx, setIdx] = useState(0);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = back
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const total = QUESTIONS.length;
  const current = QUESTIONS[idx];
  const value = answers[current?.key] || "";

  const canProceed = useMemo(() => {
    if (!current) return false;
    if (current.optional) return true;
    return value.trim().length >= 3;
  }, [current, value]);

  const setValue = (v) => setAnswers((a) => ({ ...a, [current.key]: v.slice(0, current.long ? 1200 : 400) }));

  const next = () => {
    setDirection(1);
    if (idx < total - 1) setIdx(idx + 1);
    else submit();
  };
  const back = () => { if (idx > 0) { setDirection(-1); setIdx(idx - 1); } };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/strategy-brief`, {
        email: email.trim(),
        what_you_sell: answers.what_you_sell || "",
        tried_so_far: answers.tried_so_far || "",
        worth_the_call: answers.worth_the_call || "",
        where_to_look: answers.where_to_look || "",
        how_you_heard: answers.how_you_heard || "",
      });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save just now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* --- Ask for email if we did not receive one via ?e= --- */
  if (askingEmail && !done) {
    return (
      <div className="min-h-screen bg-bg text-ink flex items-center justify-center px-6" data-testid="brief-email-gate">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} className="w-full max-w-md">
          <p className="font-sans text-[10px] uppercase tracking-mega text-brass">Strategy Brief</p>
          <h1 className="mt-4 font-serif text-4xl md:text-5xl leading-tight">Before we begin.</h1>
          <p className="mt-4 font-sans text-ink-muted leading-relaxed">
            Confirm the email you used to book the call. So Krishna can tie your notes to your intent.
          </p>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value.slice(0, 200))}
            placeholder="you@company.com"
            className="lux-field lux-field--pad-sm mt-6 w-full rounded-full"
            data-testid="brief-email-input"
            autoFocus
          />
          <button
            type="button"
            disabled={!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())}
            onClick={() => setAskingEmail(false)}
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-brass text-bg hover:bg-brass/85 px-6 py-3 font-sans text-[11px] uppercase tracking-mega font-semibold transition-colors duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="brief-email-continue"
          >
            Continue
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </motion.div>
      </div>
    );
  }

  /* --- Done state --- */
  if (done) {
    return (
      <div className="min-h-screen bg-bg text-ink flex items-center justify-center px-6 relative overflow-hidden" data-testid="brief-done">
        {/* Cinematic Affirmation Glow: expanding radial rings + ambient warm burst */}
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, ease: EASE }}
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(201,169,97,0.22) 0%, rgba(201,169,97,0.08) 25%, transparent 60%)",
          }}
        />
        {/* Three expanding rings */}
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            aria-hidden
            className="pointer-events-none absolute left-1/2 top-1/2 rounded-full border border-brass/40"
            initial={{ width: 40, height: 40, x: "-50%", y: "-50%", opacity: 0.9 }}
            animate={{ width: 900, height: 900, opacity: 0 }}
            transition={{ duration: 3.4, ease: [0.16, 0.9, 0.3, 1], delay: 0.15 + i * 0.55, repeat: Infinity, repeatDelay: 1.4 }}
          />
        ))}
        {/* Shimmer particles */}
        {[...Array(14)].map((_, i) => (
          <motion.span
            key={`p-${i}`}
            aria-hidden
            className="pointer-events-none absolute h-1 w-1 rounded-full bg-brass"
            initial={{
              x: `${20 + Math.random() * 60}vw`,
              y: `${30 + Math.random() * 40}vh`,
              opacity: 0,
              scale: 0.5,
            }}
            animate={{
              opacity: [0, 1, 0],
              y: `${20 + Math.random() * 30}vh`,
              scale: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              ease: "easeInOut",
              delay: 0.3 + i * 0.12,
              repeat: Infinity,
              repeatDelay: Math.random() * 2,
            }}
          />
        ))}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
          className="relative w-full max-w-xl text-center z-10"
        >
          {/* Check mark with pulsing halo */}
          <div className="relative mx-auto h-16 w-16">
            <motion.span
              aria-hidden
              className="absolute inset-0 rounded-full"
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: [0.6, 1.6, 1.2], opacity: [0, 0.9, 0.55] }}
              transition={{ duration: 1.4, ease: EASE, delay: 0.1 }}
              style={{ boxShadow: "0 0 60px 12px rgba(201,169,97,0.55), 0 0 24px 4px rgba(201,169,97,0.8)" }}
            />
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.35, ease: EASE }}
              className="relative h-16 w-16 rounded-full border border-brass bg-black/40 backdrop-blur-md flex items-center justify-center text-brass"
              style={{ boxShadow: "inset 0 1px 0 rgba(245,241,232,0.08), 0 0 40px rgba(201,169,97,0.35)" }}
            >
              <CheckCircle2 className="h-6 w-6" />
            </motion.div>
          </div>

          <motion.p initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.7, ease: EASE }} className="mt-7 font-sans text-[10px] uppercase tracking-mega text-brass">
            Received . Read directly
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.85, ease: EASE }} className="mt-4 font-serif text-4xl md:text-5xl leading-tight text-ink">
            Thank you.
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 1.05, ease: EASE }} className="mt-6 font-sans text-ink-muted leading-relaxed max-w-lg mx-auto">
            I read these before every call. You will get a private booking window from me within one business hour, and a short confirmation the day before we speak.
          </motion.p>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.35, ease: EASE }} className="mt-8 font-serif italic text-brass/90">
            Krishna S. . Get Virtual Growth
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 1.55, ease: EASE }}>
            <Link
              to="/"
              className="mt-10 inline-flex items-center gap-2 rounded-full border border-brass/40 hover:border-brass px-5 py-2.5 font-sans text-[11px] uppercase tracking-mega text-ink hover:text-brass transition-colors duration-500"
              data-testid="brief-return-home"
            >
              Return to Get Virtual Growth
            </Link>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  /* --- Interactive question flow --- */
  return (
    <div className="min-h-screen bg-bg text-ink relative overflow-hidden" data-testid="strategy-brief-page">
      {/* Ambient warm glow */}
      <div aria-hidden className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at 20% 20%, rgba(201,169,97,0.08), transparent 60%)" }} />

      <div className="relative max-w-3xl mx-auto px-6 md:px-10 pt-28 md:pt-36 pb-32">
        {/* Header note */}
        <div className="flex items-center justify-between gap-4 mb-14">
          <div>
            <p className="font-sans text-[10px] uppercase tracking-mega text-brass">A private note from Krishna</p>
            <p className="mt-1 font-serif italic text-ink-muted text-sm">Five short questions. Two minutes. So I show up ready.</p>
          </div>
          {/* Progress dots */}
          <div className="flex items-center gap-1.5" data-testid="brief-progress">
            {QUESTIONS.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  i < idx ? "w-6 bg-brass" : i === idx ? "w-10 bg-brass" : "w-6 bg-ink/15"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Question card with directional slide */}
        <AnimatePresence mode="wait" custom={direction} initial={false}>
          <motion.div
            key={current.key}
            custom={direction}
            variants={{
              enter: (dir) => ({ opacity: 0, x: dir > 0 ? 80 : -80, filter: "blur(6px)" }),
              center: { opacity: 1, x: 0, filter: "blur(0px)" },
              exit: (dir) => ({ opacity: 0, x: dir > 0 ? -80 : 80, filter: "blur(6px)" }),
            }}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.55, ease: EASE }}
            data-testid={`brief-q-${current.key}`}
          >
            <p className="font-sans text-[10px] uppercase tracking-mega text-brass">{current.label}{current.optional && <span className="text-ink-muted/60 normal-case tracking-normal font-serif italic ml-2">optional</span>}</p>
            <h2 className="mt-4 font-serif leading-[1.05] tracking-tight" style={{ fontSize: "clamp(1.9rem, 4vw, 3.4rem)" }}>
              {current.prompt}
            </h2>

            <div className="mt-8">
              {current.long ? (
                <textarea
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  rows={5}
                  placeholder={current.placeholder}
                  autoFocus
                  className="lux-field lux-field--pad-lg w-full rounded-2xl resize-none leading-relaxed"
                  data-testid="brief-answer-textarea"
                />
              ) : (
                <input
                  type="text"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={current.placeholder}
                  autoFocus
                  className="lux-field lux-field--pad-sm w-full rounded-full"
                  data-testid="brief-answer-input"
                />
              )}
              <p className="mt-3 font-sans text-xs text-ink-muted italic">{current.hint}</p>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Nav row */}
        <div className="mt-12 flex items-center justify-between">
          <button
            type="button"
            onClick={back}
            disabled={idx === 0}
            className="inline-flex items-center gap-2 font-sans text-[11px] uppercase tracking-mega text-ink-muted hover:text-brass disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-500"
            data-testid="brief-back"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back
          </button>

          <button
            type="button"
            onClick={next}
            disabled={!canProceed || submitting}
            className="inline-flex items-center gap-2 rounded-full bg-brass text-bg hover:bg-brass/85 px-6 py-3 font-sans text-[11px] uppercase tracking-mega font-semibold transition-colors duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="brief-next"
          >
            {idx === total - 1 ? (submitting ? "Sending..." : "Send to Krishna") : "Next"}
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Skip link for optional questions */}
        {current.optional && (
          <div className="mt-6 text-center">
            <button type="button" onClick={next} className="font-serif italic text-sm text-ink-muted hover:text-brass transition-colors" data-testid="brief-skip">
              I would rather skip this one
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
